/**
 * The postMessage protocol between the checkout frame and embed.js.
 *
 * Messages flow one way only, frame to parent. The checkout registers no
 * `message` listener at all, so a merchant page (or anything it loads) can
 * never press Pay or change what is charged: configuration reaches the frame
 * only through the iframe URL that embed.js builds. The messages are UX hints;
 * merchants fulfil orders from the webhook or an API-key status lookup.
 *
 * Shared by both bundles, so it must stay free of React and of DOM globals.
 */

export const SOURCE = 'gencoin-checkout';
export const VERSION = 1;

export const SID_RE = /^[a-f0-9]{32}$/;
export const SLUG_RE = /^[A-Za-z0-9_-]{3,100}$/;
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const MAX_RESIZE_HEIGHT = 4000;

export type LinkError = 'not_found' | 'unavailable' | 'error' | 'api_outdated';
export type FailedStatus = 'failed' | 'cancelled' | 'expired';
export type CloseReason = 'user' | 'escape' | 'done';

type Env<T extends string, D> = { source: typeof SOURCE; v: typeof VERSION; sid: string; type: T; data: D };

export type CheckoutMessage =
  | Env<'checkout.ready', { testMode: boolean; linkError: null | LinkError }>
  | Env<'checkout.resize', { height: number }>
  | Env<'checkout.payment_started', { transactionId: string | null }>
  | Env<'checkout.payment_succeeded', { transactionId: string; status: 'succeeded'; returnUrl: string | null }>
  | Env<'checkout.payment_failed', { transactionId: string | null; status: FailedStatus }>
  | Env<'checkout.close', { reason: CloseReason }>;

export type CheckoutMessageType = CheckoutMessage['type'];
export type MessageData<T extends CheckoutMessageType> = Extract<CheckoutMessage, { type: T }>['data'];

const LINK_ERRORS: readonly unknown[] = ['not_found', 'unavailable', 'error', 'api_outdated'];
const FAILED_STATUSES: readonly unknown[] = ['failed', 'cancelled', 'expired'];
const CLOSE_REASONS: readonly unknown[] = ['user', 'escape', 'done'];

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isUuid(v: unknown): v is string {
  return typeof v === 'string' && UUID_RE.test(v);
}

/** http(s) only: the parent navigates its top window to this value. */
export function isHttpUrl(v: unknown): v is string {
  if (typeof v !== 'string' || v.length > 2048) return false;
  try {
    const u = new URL(v);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
}

function isValidData(type: unknown, d: Record<string, unknown>): boolean {
  switch (type) {
    case 'checkout.ready':
      return typeof d.testMode === 'boolean' && (d.linkError === null || LINK_ERRORS.includes(d.linkError));
    case 'checkout.resize':
      return (
        typeof d.height === 'number' &&
        Number.isInteger(d.height) &&
        d.height >= 0 &&
        d.height <= MAX_RESIZE_HEIGHT
      );
    case 'checkout.payment_started':
      return d.transactionId === null || isUuid(d.transactionId);
    case 'checkout.payment_succeeded':
      return isUuid(d.transactionId) && d.status === 'succeeded' && (d.returnUrl === null || isHttpUrl(d.returnUrl));
    case 'checkout.payment_failed':
      return (d.transactionId === null || isUuid(d.transactionId)) && FAILED_STATUSES.includes(d.status);
    case 'checkout.close':
      return CLOSE_REASONS.includes(d.reason);
    default:
      return false;
  }
}

/** Full shape check of an incoming message for one open checkout (`sid`). */
export function isCheckoutMessage(data: unknown, sid: string): data is CheckoutMessage {
  if (!isRecord(data) || !SID_RE.test(sid)) return false;
  if (data.source !== SOURCE || data.v !== VERSION || data.sid !== sid) return false;
  if (!isRecord(data.data)) return false;
  return isValidData(data.type, data.data);
}

export function makeMessage<T extends CheckoutMessageType>(sid: string, type: T, data: MessageData<T>): CheckoutMessage {
  return { source: SOURCE, v: VERSION, sid, type, data } as CheckoutMessage;
}
